import { PrefetchStatus, readPrefetchStatus } from "../../../offline/prefetchStatus";

export const actionTypes = {
  SET_ONLINE: "OFFLINE_SET_ONLINE",
  SET_PENDING: "OFFLINE_SET_PENDING",
  SET_PENDING_KEYS: "OFFLINE_SET_PENDING_KEYS",
  SET_PREFETCH: "OFFLINE_SET_PREFETCH",
} as const;

interface InitialState {
  isOnline: boolean;
  pendingCount: number;
  failedCount: number;
  /** entityKey of every row still waiting to sync — drives the per-row badges. */
  pendingKeys: string[];
  /** entityKey of every row that gave up and needs a manual retry. */
  failedKeys: string[];
  /** Progress and freshness of the "Download for offline" snapshot. */
  prefetch: PrefetchStatus;
}

const initialState: InitialState = {
  // Assume online until proven otherwise: starting pessimistic would flash the
  // banner on every cold boot, including for users who never lose signal.
  isOnline: true,
  pendingCount: 0,
  failedCount: 0,
  pendingKeys: [],
  failedKeys: [],
  // Seeded from the persisted snapshot, not from the never-downloaded default.
  // Nothing else rehydrates it: when the snapshot is fresh, runPrefetchIfStale
  // returns without publishing, so the card would read "Not downloaded yet" after
  // every reload on a fully covered phone — and offline it would read "Connect to
  // the internet to download", leaving a volunteer with no way to confirm their
  // coverage in the field. Safe to trust because the engine only ever writes this
  // key from its `finally` block, so the stored value is always terminal, never a
  // half-finished "running". readPrefetchStatus() builds a fresh object on every
  // call, which keeps the shared DEFAULT_STATUS singleton out of the store.
  prefetch: readPrefetchStatus(),
};

export const offlineReducer = (state = initialState, action: any): InitialState => {
  switch (action.type) {
    case actionTypes.SET_ONLINE:
      return { ...state, isOnline: action.payload };
    case actionTypes.SET_PENDING:
      return {
        ...state,
        pendingCount: action.payload.pendingCount,
        failedCount: action.payload.failedCount,
      };
    case actionTypes.SET_PENDING_KEYS:
      return {
        ...state,
        pendingKeys: action.payload.pendingKeys,
        failedKeys: action.payload.failedKeys,
      };
    case actionTypes.SET_PREFETCH:
      return { ...state, prefetch: action.payload };
    default:
      return state;
  }
};

export const offlineActions = {
  setOnlineAC: (isOnline: boolean) => ({
    type: actionTypes.SET_ONLINE,
    payload: isOnline,
  }),
  setPendingAC: (pendingCount: number, failedCount: number) => ({
    type: actionTypes.SET_PENDING,
    payload: { pendingCount, failedCount },
  }),
  setPendingKeysAC: (pendingKeys: string[], failedKeys: string[]) => ({
    type: actionTypes.SET_PENDING_KEYS,
    payload: { pendingKeys, failedKeys },
  }),
  setPrefetchAC: (status: PrefetchStatus) => ({
    type: actionTypes.SET_PREFETCH,
    payload: status,
  }),
};
